import React, { useMemo } from 'react';
import PlaybookStatusBadge from './PlaybookStatusBadge';
import type { PlaybookInstance, AvailableTransition } from '../types/playbook.types';

interface PlaybookCoordinationViewProps {
  instances: PlaybookInstance[];
  transitionsByInstance: Record<string, AvailableTransition[]>;
}

interface Dependency {
  fromInstanceId: string;
  fromPlaybookName: string;
  toInstanceId: string;
  toPlaybookName: string;
  requiredStatus: string;
  blockedTransitionName: string;
}

const SIBLING_PATTERN = /(?:Waiting for|Requires)\s+(.+?)\s+(?:playbook|workflow)\s+.*?['"](.+?)['"]/i;

const PlaybookCoordinationView: React.FC<PlaybookCoordinationViewProps> = ({
  instances,
  transitionsByInstance,
}) => {
  const dependencies = useMemo(() => {
    const deps: Dependency[] = [];
    const instanceByName: Record<string, PlaybookInstance> = {};

    for (const inst of instances) {
      if (inst.playbook?.name) {
        instanceByName[inst.playbook.name.toLowerCase()] = inst;
      }
    }

    for (const inst of instances) {
      const transitions = transitionsByInstance[inst.id] || [];
      for (const t of transitions) {
        if (!t.isAllowed && t.blockedReason) {
          const match = t.blockedReason.match(SIBLING_PATTERN);
          if (match) {
            const siblingName = match[1];
            const requiredStatus = match[2];
            const sibling = instanceByName[siblingName.toLowerCase()];
            if (sibling && sibling.id !== inst.id) {
              deps.push({
                fromInstanceId: inst.id,
                fromPlaybookName: inst.playbook?.name || 'Playbook',
                toInstanceId: sibling.id,
                toPlaybookName: sibling.playbook?.name || 'Playbook',
                requiredStatus,
                blockedTransitionName: t.name,
              });
            }
          }
        }
      }
    }

    return deps;
  }, [instances, transitionsByInstance]);

  if (dependencies.length === 0) return null;

  return (
    <div className="rounded-lg border border-lex-accent bg-lex-accent-soft p-4">
      <h4 className="text-sm font-semibold text-lex-accent mb-3">
        Cross-Playbook Dependencies
      </h4>
      <div className="space-y-2">
        {dependencies.map((dep, i) => {
          const sibling = instances.find((inst) => inst.id === dep.toInstanceId);
          return (
            <div
              key={i}
              className="flex flex-wrap items-center gap-2 rounded-md border border-lex-accent bg-white dark:bg-gray-800 px-3 py-2 text-sm"
            >
              <span className="font-medium text-gray-900 dark:text-white">{dep.fromPlaybookName}</span>
              <span className="text-gray-400">&rarr;</span>
              <span className="text-gray-600 dark:text-gray-400">requires</span>
              <span className="font-medium text-lex-accent">{dep.toPlaybookName}</span>
              <span className="text-gray-600 dark:text-gray-400">at</span>
              <span className="font-medium text-gray-900 dark:text-white">&lsquo;{dep.requiredStatus}&rsquo;</span>
              {sibling?.currentStatus && (
                <>
                  <span className="text-gray-400 ml-1">currently:</span>
                  <PlaybookStatusBadge
                    category={sibling.currentStatus.category}
                    name={sibling.currentStatus.name}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlaybookCoordinationView;
